import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCENT = "#BACD1C";

function Stat({ label, value, testid }) {
  return (
    <div data-testid={testid} className="border border-white/10 bg-white/[0.02] p-5">
      <div className="text-[11px] uppercase tracking-[0.2em] text-white/40 font-mono">{label}</div>
      <div className="mt-2 text-3xl font-bold font-mono" style={{ color: ACCENT }}>
        {value?.toLocaleString?.() ?? value}
      </div>
    </div>
  );
}

export default function MailAdmin() {
  const [key, setKey] = useState(localStorage.getItem("tq_admin_key") || "");
  const [input, setInput] = useState("");
  const [summary, setSummary] = useState(null);
  const [subs, setSubs] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState("");

  const load = async (k) => {
    setLoading(true);
    setError("");
    try {
      const [s, l] = await Promise.all([
        axios.get(`${API}/admin/mail/summary`, { headers: { "X-Admin-Key": k } }),
        axios.get(`${API}/admin/mail/subscribers?limit=500`, { headers: { "X-Admin-Key": k } }),
      ]);
      setSummary(s.data);
      setSubs(l.data.subscribers || []);
      localStorage.setItem("tq_admin_key", k);
      setKey(k);
    } catch (e) {
      if (e?.response?.status === 401) {
        setError("Invalid admin key.");
        localStorage.removeItem("tq_admin_key");
        setKey("");
      } else {
        setError("Could not load. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const resend = async (email) => {
    setResending(email);
    try {
      await axios.post(`${API}/admin/mail/resend-welcome`, { email }, { headers: { "X-Admin-Key": key } });
      toast.success(`Welcome email re-sent to ${email}`);
      load(key);
    } catch (e) {
      toast.error("Failed to send. Try again.");
    } finally {
      setResending("");
    }
  };

  useEffect(() => {
    if (key) load(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!key || (!summary && !loading)) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6" data-testid="mail-login">
        <div className="w-full max-w-sm border border-white/10 bg-white/[0.02] p-8">
          <div className="text-xs uppercase tracking-[0.3em] font-mono mb-1" style={{ color: ACCENT }}>TradrQuant</div>
          <h1 className="text-xl font-bold mb-6 font-mono">Mailing Control</h1>
          <label className="text-[11px] uppercase tracking-[0.2em] text-white/40 font-mono">Admin key</label>
          <input
            data-testid="mail-admin-key-input"
            type="password"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load(input.trim())}
            className="mt-2 w-full bg-black border border-white/15 px-3 py-2 font-mono text-sm focus:outline-none focus:border-[#BACD1C]"
            placeholder="enter key"
            autoFocus
          />
          {error && <div className="mt-3 text-xs text-red-400 font-mono" data-testid="mail-admin-error">{error}</div>}
          <button
            data-testid="mail-admin-login-btn"
            onClick={() => load(input.trim())}
            disabled={loading || !input.trim()}
            className="mt-5 w-full py-2.5 font-mono text-sm font-bold text-black transition-opacity disabled:opacity-40"
            style={{ background: ACCENT }}
          >
            {loading ? "LOADING…" : "OPEN MAILING"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white p-6 md:p-10" data-testid="mail-dashboard">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] font-mono" style={{ color: ACCENT }}>TradrQuant · Mailing</div>
            <h1 className="text-2xl font-bold font-mono mt-1">Subscribers & Referrals</h1>
          </div>
          <div className="flex items-center gap-3">
            <a href="/admin/analytics" className="text-xs font-mono text-white/40 hover:text-[#BACD1C]">TRAFFIC →</a>
            <button data-testid="mail-refresh-btn" onClick={() => load(key)} className="text-xs font-mono border border-white/15 px-4 py-2 hover:border-[#BACD1C]">
              {loading ? "…" : "REFRESH"}
            </button>
            <button
              data-testid="mail-logout-btn"
              onClick={() => { localStorage.removeItem("tq_admin_key"); setKey(""); setSummary(null); }}
              className="text-xs font-mono text-white/40 hover:text-white"
            >
              LOG OUT
            </button>
          </div>
        </div>

        {summary && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <Stat label="Subscribers" value={summary.total} testid="stat-subscribers" />
              <Stat label="Welcomed" value={summary.welcomed} testid="stat-welcomed" />
              <Stat label="Via referral" value={summary.referred} testid="stat-referred" />
              <Stat label="Joined today" value={summary.today} testid="stat-today" />
            </div>
            <div className="mb-10 border border-[#BACD1C]/20 bg-[#BACD1C]/[0.03] px-4 py-3 font-mono text-[11px] text-white/45">
              Welcome emails are sent automatically at signup (transactional). Bulk / newsletter blasts are not available on this integration.
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* leaderboard */}
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-white/40 font-mono mb-3">Referral leaderboard</div>
                <div className="border border-white/10 divide-y divide-white/5" data-testid="referral-leaderboard">
                  {summary.leaderboard?.length ? (
                    summary.leaderboard.map((r, i) => (
                      <div key={r.ref_code} className="flex items-center justify-between px-4 py-2.5 font-mono text-sm">
                        <span className="text-white/70 truncate mr-3"><span className="text-white/30 mr-2">{i + 1}.</span>{r.email}</span>
                        <span style={{ color: ACCENT }}>{r.referrals}</span>
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-white/30 font-mono text-sm">No referrals yet.</div>
                  )}
                </div>
              </div>

              {/* subscriber list */}
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-white/40 font-mono mb-3">
                  Subscribers ({subs.length})
                </div>
                <div className="border border-white/10 divide-y divide-white/5 max-h-[520px] overflow-y-auto" data-testid="subscriber-list">
                  {subs.length ? (
                    subs.map((s) => (
                      <div key={s.email} className="flex items-center justify-between gap-3 px-4 py-2.5 font-mono text-xs">
                        <div className="min-w-0">
                          <div className="text-white/80 truncate">{s.email}</div>
                          <div className="text-white/30 text-[10px] mt-0.5">
                            {new Date(s.created_at).toLocaleDateString()} · refs {s.referrals}
                            {s.referred_by ? " · invited" : ""} {s.welcomed ? "" : "· ✉︎ pending"}
                          </div>
                        </div>
                        <button
                          data-testid={`resend-${s.email}`}
                          onClick={() => resend(s.email)}
                          disabled={resending === s.email}
                          className="shrink-0 border border-white/15 px-2.5 py-1.5 text-[10px] uppercase tracking-[0.1em] text-white/60 hover:border-[#BACD1C] hover:text-[#BACD1C] disabled:opacity-40 transition-colors"
                        >
                          {resending === s.email ? "…" : "Resend"}
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-white/30 font-mono text-sm">No subscribers yet.</div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-10 text-[10px] text-white/25 font-mono">Updated {new Date(summary.updated_at).toLocaleString()}</div>
          </>
        )}
      </div>
    </div>
  );
}
